// src/pages/Coupons/Coupons.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination, InputGroup, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaTag, FaCalendarAlt, FaPercent, FaDollarSign, FaToggleOn, FaToggleOff, FaEye, FaEyeSlash } from 'react-icons/fa';
import { couponsAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Coupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [filteredCoupons, setFilteredCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    // Statistics state
    const [couponStats, setCouponStats] = useState({
        total: 0,
        active: 0,
        expired: 0,
        upcoming: 0,
        inactive: 0,
        total_usage: 0
    });

    const [formData, setFormData] = useState({
        code: '',
        description: '',
        discount_type: 'percent',
        discount_value: '',
        starts_at: '',
        ends_at: '',
        usage_limit: '',
        active: '1'  // Changed from is_active to active
    });

    useEffect(() => {
        fetchCoupons();
        fetchStats();
    }, [pagination.page, statusFilter]);

    useEffect(() => {
        const handleSearch = async () => {
            if (searchTerm.trim()) {
                try {
                    setLoading(true);
                    const response = await couponsAPI.search({ search: searchTerm });
                    console.log('Search API Response:', response);

                    if (response.data) {
                        // Handle different response structures
                        let searchData = [];
                        if (response.data.data && Array.isArray(response.data.data)) {
                            searchData = response.data.data;
                        } else if (Array.isArray(response.data)) {
                            searchData = response.data;
                        }
                        setFilteredCoupons(searchData);
                    } else {
                        // Fallback to local filtering
                        const filtered = coupons.filter(coupon => {
                            const searchLower = searchTerm.toLowerCase();
                            return (
                                coupon.code?.toLowerCase().includes(searchLower) ||
                                coupon.description?.toLowerCase().includes(searchLower)
                            );
                        });
                        setFilteredCoupons(filtered);
                    }
                } catch (err) {
                    console.error('Error searching coupons:', err);
                    // Fallback to local filtering
                    const filtered = coupons.filter(coupon => {
                        const searchLower = searchTerm.toLowerCase();
                        return (
                            coupon.code?.toLowerCase().includes(searchLower) ||
                            coupon.description?.toLowerCase().includes(searchLower)
                        );
                    });
                    setFilteredCoupons(filtered);
                } finally {
                    setLoading(false);
                }
            } else {
                setFilteredCoupons(coupons);
            }
        };

        // Debounce search
        const timeoutId = setTimeout(() => {
            handleSearch();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, coupons]);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(statusFilter !== 'all' && { status: statusFilter })
            };

            console.log('Fetching coupons with params:', params);
            const response = await couponsAPI.list(params);

            console.log('Coupons API Response:', response);

            let couponsData = [];
            let paginationData = {};

            if (response.data) {
                // Handle different response structures
                if (response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
                    // Response structure: { data: { data: [...], meta: {...} } }
                    couponsData = response.data.data.data;
                    paginationData = response.data.data.meta || {};
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    // Response structure: { data: [...], meta: {...} }
                    couponsData = response.data.data;
                    paginationData = response.data.meta || {};
                } else if (Array.isArray(response.data)) {
                    // Response structure: [...]
                    couponsData = response.data;
                }
            }

            if (!Array.isArray(couponsData)) {
                console.error('Invalid coupons data format:', response.data);
                couponsData = [];
            }

            console.log('Processed coupons data:', couponsData);

            setCoupons(couponsData);
            setFilteredCoupons(couponsData);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || couponsData.length,
                pages: paginationData.total_pages || paginationData.pages || Math.ceil((paginationData.total || couponsData.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching coupons:', err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch coupons');
            setCoupons([]);
            setFilteredCoupons([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await couponsAPI.stats();
            console.log('Stats API Response:', response);

            if (response.data) {
                if (response.data.data) {
                    setCouponStats(response.data.data || {});
                } else if (response.data.stats) {
                    setCouponStats(response.data.stats || {});
                } else {
                    // Calculate from local data
                    calculateLocalStats();
                }
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
            // Use local calculation as fallback
            calculateLocalStats();
        }
    };

    const calculateLocalStats = () => {
        const stats = {
            total: coupons.length,
            active: coupons.filter(c => c.active === 1).length,
            expired: coupons.filter(c => getCouponStatus(c) === 'expired').length,
            upcoming: coupons.filter(c => getCouponStatus(c) === 'upcoming').length,
            inactive: coupons.filter(c => c.active === 0).length,
            total_usage: coupons.reduce((total, coupon) => total + (parseInt(coupon.used_count) || 0), 0)
        };
        setCouponStats(stats);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            // Validation
            if (!formData.code || !formData.description || !formData.discount_value) {
                setError('Code, description, and discount value are required');
                return;
            }

            if (formData.discount_type === 'percent' && (formData.discount_value < 1 || formData.discount_value > 100)) {
                setError('Percentage discount must be between 1 and 100');
                return;
            }

            if (formData.usage_limit && formData.usage_limit < 1) {
                setError('Usage limit must be at least 1');
                return;
            }

            // Format dates
            const couponData = {
                code: formData.code.toUpperCase(),
                description: formData.description,
                discount_type: formData.discount_type,
                discount_value: parseFloat(formData.discount_value),
                starts_at: formData.starts_at || null,
                ends_at: formData.ends_at || null,
                usage_limit: formData.usage_limit || null,
                active: parseInt(formData.active) // Changed from is_active to active
            };

            console.log('Creating coupon with data:', couponData);

            const response = await couponsAPI.create(couponData);
            console.log('Create API Response:', response);

            if (response.data) {
                setSuccessMessage('Coupon created successfully!');
                setShowCreateModal(false);
                resetForm();
                await fetchCoupons();
                await fetchStats();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to create coupon');
            }

        } catch (err) {
            console.error('Error creating coupon:', err);
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create coupon');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedCoupon?.id) {
            setError('Coupon ID is missing');
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            // Validation
            if (!formData.code || !formData.description || !formData.discount_value) {
                setError('Code, description, and discount value are required');
                return;
            }

            if (formData.discount_type === 'percent' && (formData.discount_value < 1 || formData.discount_value > 100)) {
                setError('Percentage discount must be between 1 and 100');
                return;
            }

            if (formData.usage_limit && formData.usage_limit < 1) {
                setError('Usage limit must be at least 1');
                return;
            }

            // Format dates
            const couponData = {
                code: formData.code.toUpperCase(),
                description: formData.description,
                discount_type: formData.discount_type,
                discount_value: parseFloat(formData.discount_value),
                starts_at: formData.starts_at || null,
                ends_at: formData.ends_at || null,
                usage_limit: formData.usage_limit || null,
                active: parseInt(formData.active) // Changed from is_active to active
            };

            console.log('Updating coupon with data:', couponData);

            const response = await couponsAPI.update(selectedCoupon.id, couponData);
            console.log('Update API Response:', response);

            if (response.data) {
                setSuccessMessage('Coupon updated successfully!');
                setShowEditModal(false);
                resetForm();
                setSelectedCoupon(null);
                setIsEditing(false);
                await fetchCoupons();
                await fetchStats();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to update coupon');
            }

        } catch (err) {
            console.error('Error updating coupon:', err);
            let errorMsg = 'Failed to update coupon';
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.message) {
                errorMsg = err.message;
            }
            setError(errorMsg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedCoupon?.id) {
            setError('Coupon ID is missing');
            setShowDeleteModal(false);
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Deleting coupon with ID:', selectedCoupon.id);

            const response = await couponsAPI.delete(selectedCoupon.id);
            console.log('Delete API Response:', response);

            if (response.status === 200 || response.status === 204 || response.data) {
                setSuccessMessage('Coupon deleted successfully!');

                // Remove from local state immediately
                setCoupons(prev => prev.filter(coupon => coupon.id !== selectedCoupon.id));
                setFilteredCoupons(prev => prev.filter(coupon => coupon.id !== selectedCoupon.id));

                // Refresh data
                await fetchStats();

                setShowDeleteModal(false);
                setSelectedCoupon(null);
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to delete coupon');
            }

        } catch (err) {
            console.error('Error deleting coupon:', err);
            setError(err.response?.data?.message || err.message || 'Failed to delete coupon');
        } finally {
            setActionLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleStatusToggle = async (couponId, currentActive) => {
        try {
            setActionLoading(true);

            console.log(`Toggling status for coupon ${couponId}, current active: ${currentActive}`);

            let response;
            if (currentActive === 1) {
                // Deactivate the coupon
                response = await couponsAPI.deactivate(couponId);
                console.log('Deactivate API Response:', response);
                setSuccessMessage('Coupon deactivated successfully!');
            } else {
                // Activate the coupon
                response = await couponsAPI.activate(couponId);
                console.log('Activate API Response:', response);
                setSuccessMessage('Coupon activated successfully!');
            }

            // Check if API call was successful
            if (response && response.data) {
                // Update local state with the response data
                const updatedCoupon = response.data.data || response.data;

                setCoupons(prev => prev.map(coupon =>
                    coupon.id === couponId ? { ...coupon, ...updatedCoupon } : coupon
                ));
                setFilteredCoupons(prev => prev.map(coupon =>
                    coupon.id === couponId ? { ...coupon, ...updatedCoupon } : coupon
                ));

                // Also refresh stats
                await fetchStats();

                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                // If API doesn't return updated data, refetch
                await fetchCoupons();
                await fetchStats();
                setSuccessMessage(`Coupon ${currentActive === 1 ? 'deactivated' : 'activated'} successfully!`);
                setTimeout(() => setSuccessMessage(''), 3000);
            }

        } catch (err) {
            console.error('Error changing coupon status:', err);
            setError(err.response?.data?.message || err.message || 'Failed to change coupon status');
        } finally {
            setActionLoading(false);
        }
    };

    const openEditModal = (coupon) => {
        setSelectedCoupon(coupon);
        setFormData({
            code: coupon.code || '',
            description: coupon.description || '',
            discount_type: coupon.discount_type || 'percent',
            discount_value: coupon.discount_value || '',
            starts_at: coupon.starts_at ? formatDateForInput(coupon.starts_at) : '',
            ends_at: coupon.ends_at ? formatDateForInput(coupon.ends_at) : '',
            usage_limit: coupon.usage_limit || '',
            active: coupon.active?.toString() || '1' // Changed from is_active to active
        });
        setIsEditing(true);
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (coupon) => {
        console.log('Opening delete modal for coupon:', coupon);
        setSelectedCoupon(coupon);
        setShowDeleteModal(true);
        setError('');
    };

    const resetForm = () => {
        setFormData({
            code: '',
            description: '',
            discount_type: 'percent',
            discount_value: '',
            starts_at: '',
            ends_at: '',
            usage_limit: '',
            active: '1' // Changed from is_active to active
        });
        setIsEditing(false);
        setSelectedCoupon(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const getCouponStatus = (coupon) => {
        // First check the active field from API
        if (coupon.active === 0) return 'inactive';

        // Then check dates if coupon is active
        const now = new Date();
        const startDate = coupon.starts_at ? parseDate(coupon.starts_at) : null;
        const endDate = coupon.ends_at ? parseDate(coupon.ends_at) : null;

        // Check dates only if coupon is active
        if (startDate && now < startDate) return 'upcoming';
        if (endDate && now > endDate) return 'expired';

        return 'active';
    };

    const parseDate = (dateString) => {
        if (!dateString) return null;
        try {
            // Handle different date formats
            if (dateString.includes('T')) {
                return new Date(dateString);
            } else if (dateString.includes('-')) {
                // Check if it's just a date (YYYY-MM-DD) or includes time
                if (dateString.length === 10) {
                    // Just date, add time to avoid timezone issues
                    return new Date(dateString + 'T00:00:00');
                }
                return new Date(dateString);
            } else {
                return new Date(dateString);
            }
        } catch {
            console.error('Error parsing date:', dateString);
            return null;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge bg="success" className="px-3 py-2">Active</Badge>;
            case 'inactive':
                return <Badge bg="secondary" className="px-3 py-2">Inactive</Badge>;
            case 'upcoming':
                return <Badge bg="warning" text="dark" className="px-3 py-2">Upcoming</Badge>;
            case 'expired':
                return <Badge bg="danger" className="px-3 py-2">Expired</Badge>;
            default:
                return <Badge bg="secondary" className="px-3 py-2">Unknown</Badge>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = parseDate(dateString);
            if (!date || isNaN(date.getTime())) return dateString;

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = parseDate(dateString);
            if (!date || isNaN(date.getTime())) return '';

            // Format as YYYY-MM-DD for date input
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        } catch {
            return '';
        }
    };

    const formatDiscount = (coupon) => {
        if (!coupon) return '';
        if (coupon.discount_type === 'percent') {
            return `${coupon.discount_value}%`;
        } else {
            return `$${parseFloat(coupon.discount_value).toFixed(2)}`;
        }
    };

    const getDiscountIcon = (discountType) => {
        return discountType === 'percent' ? <FaPercent /> : <FaDollarSign />;
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    if (loading && !actionLoading) return (
        <div className="text-center py-5">
            <Spinner animation="border" variant="success" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2 text-muted">Loading coupons...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Coupons Management</h1>
                    <p className="text-muted mb-0">Manage discount coupons and promotional offers</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center me-2"
                        disabled={actionLoading}
                    >
                        <FaPlus className="me-2" />
                        Create Coupon
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <strong>Error:</strong> {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    <strong>Success:</strong> {successMessage}
                </Alert>
            )}

            {/* Stats Cards */}
            <Row className="mb-4 g-3">
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-primary text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{couponStats.total}</div>
                            <small>Total Coupons</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-success text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{couponStats.active}</div>
                            <small>Active</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-warning text-dark">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{couponStats.upcoming}</div>
                            <small>Upcoming</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-danger text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{couponStats.expired}</div>
                            <small>Expired</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-secondary text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{couponStats.inactive}</div>
                            <small>Inactive</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-info text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{couponStats.total_usage || 0}</div>
                            <small>Total Used</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search coupons by code or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                    disabled={actionLoading}
                                />
                            </div>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small mb-1 txt-cl" >Filter by Status</Form.Label>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    size="sm"
                                    disabled={actionLoading}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="upcoming">Upcoming</option>
                                    <option value="expired">Expired</option>
                                    <option value="inactive">Inactive</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{filteredCoupons.length}</strong> coupons
                                </small>
                            </div>
                            {(searchTerm || statusFilter !== 'all') && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('all');
                                    }}
                                    disabled={actionLoading}
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th width="60"></th>
                                <th>Coupon Code</th>
                                <th>Description</th>
                                <th>Discount</th>
                                <th>Validity Period</th>
                                <th>Usage</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCoupons.length > 0 ? (
                                filteredCoupons.map(coupon => {
                                    const status = getCouponStatus(coupon);
                                    return (
                                        <tr key={coupon.id}>
                                            <td>
                                                <div
                                                    className="bg-warning rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <FaTag className="text-white" size={16} />
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-semibold">
                                                        <code className="fs-6">{coupon.code}</code>
                                                    </div>
                                                    <small className="text-muted">
                                                        ID: {coupon.id?.substring(0, 12)}...
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div>{coupon.description}</div>
                                                    <small className="text-muted text-capitalize">
                                                        {coupon.discount_type} discount
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="me-2">
                                                        {getDiscountIcon(coupon.discount_type)}
                                                    </div>
                                                    <div className="fw-bold">
                                                        {formatDiscount(coupon)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div>
                                                        <FaCalendarAlt className="me-1" />
                                                        <strong>Start:</strong> {formatDate(coupon.starts_at)}
                                                    </div>
                                                    <div>
                                                        <FaCalendarAlt className="me-1" />
                                                        <strong>End:</strong> {formatDate(coupon.ends_at)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="text-center">
                                                    {coupon.usage_limit ? (
                                                        <>
                                                            <div className="fw-semibold">
                                                                {coupon.used_count || 0} / {coupon.usage_limit}
                                                            </div>
                                                            <small className="text-muted">uses</small>
                                                            <div className="progress mt-1" style={{ height: '5px' }}>
                                                                <div
                                                                    className="progress-bar bg-success"
                                                                    role="progressbar"
                                                                    style={{
                                                                        width: `${Math.min(100, ((parseInt(coupon.used_count) || 0) / coupon.usage_limit) * 100)}%`
                                                                    }}
                                                                ></div>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="fw-semibold">{coupon.used_count || 0}</div>
                                                            <small className="text-muted">used (unlimited)</small>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {getStatusBadge(status)}
                                                    <Button
                                                        variant={coupon.active === 1 ? "outline-success" : "outline-secondary"}
                                                        size="sm"
                                                        className="p-1 d-flex align-items-center"
                                                        onClick={() => handleStatusToggle(coupon.id, coupon.active)}
                                                        title={coupon.active === 1 ? 'Deactivate coupon' : 'Activate coupon'}
                                                        disabled={actionLoading}
                                                    >
                                                        {coupon.active === 1 ? (
                                                            <>
                                                                <FaToggleOn className="me-1" size={20} />
                                                                <span className="small">Active</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaToggleOff className="me-1" size={20} />
                                                                <span className="small">Inactive</span>
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div>{formatDate(coupon.created_at)}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => openEditModal(coupon)}
                                                        title="Edit Coupon"
                                                        disabled={actionLoading}
                                                    >
                                                        <FaEdit className="me-1" />
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => openDeleteModal(coupon)}
                                                        title="Delete Coupon"
                                                        disabled={actionLoading}
                                                    >
                                                        <FaTrash className="me-1" />
                                                        Delete
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm || statusFilter !== 'all' ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No coupons found matching your filters</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaTag className="h3 mb-3" />
                                                    <p>No coupons found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {filteredCoupons.length} of {pagination.total} coupons
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Coupon"
                onSubmit={handleCreate}
                submitText={actionLoading ? "Creating..." : "Create Coupon"}
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Coupon Code *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                required
                                placeholder="GOLF15"
                                disabled={actionLoading}
                                maxLength={20}
                            />
                            <Form.Text className="text-muted">
                                Unique code that customers will use (uppercase, alphanumeric)
                            </Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Description *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                required
                                placeholder="Golf Season Special 15% Off"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Discount Type *</Form.Label>
                            <Form.Select
                                value={formData.discount_type}
                                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                                required
                                disabled={actionLoading}
                            >
                                <option value="percent">Percentage (%)</option>
                                <option value="fixed">Fixed Amount ($)</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Discount Value *</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>
                                    {formData.discount_type === 'percent' ? <FaPercent /> : <FaDollarSign />}
                                </InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={formData.discount_value}
                                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                                    required
                                    placeholder={formData.discount_type === 'percent' ? '15' : '50.00'}
                                    min="0"
                                    step={formData.discount_type === 'percent' ? '1' : '0.01'}
                                    disabled={actionLoading}
                                />
                            </InputGroup>
                            {formData.discount_type === 'percent' && (
                                <Form.Text className="text-muted">
                                    Enter a value between 1 and 100
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.starts_at}
                                onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Leave empty for immediate start
                            </Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.ends_at}
                                onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Leave empty for no expiry
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Usage Limit</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.usage_limit}
                                onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                                placeholder="Leave empty for unlimited"
                                min="1"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Maximum number of times this coupon can be used
                            </Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.active}
                                onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                                disabled={actionLoading}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit Coupon: ${selectedCoupon?.code || ''}`}
                onSubmit={handleUpdate}
                submitText={actionLoading ? "Updating..." : "Update Coupon"}
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Coupon Code *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                required
                                placeholder="GOLF15"
                                disabled={actionLoading}
                                maxLength={20}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Description *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                required
                                placeholder="Golf Season Special 15% Off"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Discount Type *</Form.Label>
                            <Form.Select
                                value={formData.discount_type}
                                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                                required
                                disabled={actionLoading}
                            >
                                <option value="percent">Percentage (%)</option>
                                <option value="fixed">Fixed Amount ($)</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Discount Value *</Form.Label>
                            <InputGroup>
                                <InputGroup.Text>
                                    {formData.discount_type === 'percent' ? <FaPercent /> : <FaDollarSign />}
                                </InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    value={formData.discount_value}
                                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                                    required
                                    placeholder={formData.discount_type === 'percent' ? '15' : '50.00'}
                                    min="0"
                                    step={formData.discount_type === 'percent' ? '1' : '0.01'}
                                    disabled={actionLoading}
                                />
                            </InputGroup>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.starts_at}
                                onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.ends_at}
                                onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Usage Limit</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.usage_limit}
                                onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                                placeholder="Leave empty for unlimited"
                                min="1"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.active}
                                onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                                disabled={actionLoading}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Coupon"
                message={
                    <div>
                        <p>Are you sure you want to delete coupon <strong>"{selectedCoupon?.code}"</strong>?</p>
                        <div className="alert alert-warning small">
                            <strong>Warning:</strong> This action cannot be undone. Customers will no longer be able to use this coupon.
                        </div>
                        <div className="small">
                            <strong>Coupon Details:</strong>
                            <ul className="mb-0">
                                <li>Description: {selectedCoupon?.description}</li>
                                <li>Discount: {selectedCoupon && formatDiscount(selectedCoupon)}</li>
                                <li>Status: {selectedCoupon && getStatusBadge(getCouponStatus(selectedCoupon))}</li>
                                <li>Used: {selectedCoupon?.used_count || 0} times</li>
                            </ul>
                        </div>
                        <p className="text-muted small mt-2 mb-0">
                            Coupon ID: <code>{selectedCoupon?.id}</code>
                        </p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading}
                variant="danger"
                loading={actionLoading}
            />
        </div>
    );
};

export default Coupons;